import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "clientType",
    "companyFields",
    "individualFields",
    "companyEmailFields",
    "invoiceEmailField",
    "organizationNumber",
    "deliveryAddress",
    "sameAsMainField",
    "deliveryMap",
    "deliveryLatitude",
    "deliveryLongitude",
    "mainPhone",
    "mainPhoneField"
  ]

  connect() {
    this.toggleFields()
    this.initializeDeliveryAddress()
    // Update main phone on page load if already filled
    this.updateMainPhoneDelayed()
  }

  initializeDeliveryAddress() {
    const checkbox = document.getElementById('sameAsMain')
    if (checkbox && checkbox.checked) {
      this.deliveryAddressTarget.classList.add('d-none')
      this.disableFields(this.deliveryAddressTarget)
    }
  }

  toggleFields() {
    const clientType = this.clientTypeTarget.value

    if (clientType == 'company' && clientType !== '') {
      this.showCompanyFields()
      this.hideIndividualFields()
      this.showCompanyEmailFields()
    } else if (clientType && clientType !== '') {
      this.hideCompanyFields()
      this.showIndividualFields()
      this.hideCompanyEmailFields()
    } else {
      this.hideCompanyFields()
      this.hideIndividualFields()
      this.hideCompanyEmailFields()
    }
  }

  showCompanyFields() {
    this.companyFieldsTarget.classList.remove('d-none')
    this.enableFields(this.companyFieldsTarget)
  }

  hideCompanyFields() {
    this.companyFieldsTarget.classList.add('d-none')
    this.disableFields(this.companyFieldsTarget)
  }

  showIndividualFields() {
    this.individualFieldsTarget.classList.remove('d-none')
    this.enableFields(this.individualFieldsTarget)
  }

  hideIndividualFields() {
    this.individualFieldsTarget.classList.add('d-none')
    this.disableFields(this.individualFieldsTarget)
  }

  showCompanyEmailFields() {
    if (this.hasCompanyEmailFieldsTarget) {
      this.companyEmailFieldsTarget.classList.remove('d-none')
      this.enableFields(this.companyEmailFieldsTarget)
    }
  }

  hideCompanyEmailFields() {
    if (this.hasCompanyEmailFieldsTarget) {
      this.companyEmailFieldsTarget.classList.add('d-none')
      this.disableFields(this.companyEmailFieldsTarget)
    }
  }

  toggleInvoiceEmail(event) {
    const isChecked = event.target.checked
    console.log('toggleInvoiceEmail - checkbox checked:', isChecked)

    if (isChecked) {
      // Copy main email to invoice email
      const mainEmail = document.querySelector('input[name="client_registration[email]"]')?.value || ''
      const invoiceEmailInput = document.querySelector('input[name="client_registration[invoice_email]"]')

      if (invoiceEmailInput) {
        invoiceEmailInput.value = mainEmail
        invoiceEmailInput.disabled = true
      }

      // Hide invoice email field
      if (this.hasInvoiceEmailFieldTarget) {
        this.invoiceEmailFieldTarget.classList.add('d-none')
      }
    } else {
      // Enable and show invoice email field
      const invoiceEmailInput = document.querySelector('input[name="client_registration[invoice_email]"]')
      if (invoiceEmailInput) {
        invoiceEmailInput.disabled = false
      }

      if (this.hasInvoiceEmailFieldTarget) {
        this.invoiceEmailFieldTarget.classList.remove('d-none')
      }
    }
  }

  enableFields(container) {
    const inputs = container.querySelectorAll('input, select, textarea')
    inputs.forEach(input => input.disabled = false)
  }

  disableFields(container) {
    const inputs = container.querySelectorAll('input, select, textarea')
    inputs.forEach(input => input.disabled = true)
  }

  toggleDeliveryAddress(event) {
    const isChecked = event.target.checked
    console.log('toggleDeliveryAddress - checkbox checked:', isChecked)

    if (isChecked) {
      // Hide and disable delivery address fields
      this.deliveryAddressTarget.classList.add('d-none')
      this.disableFields(this.deliveryAddressTarget)
      this.sameAsMainFieldTarget.value = 'true'

      // Copy main address to delivery address fields
      this.copyMainAddressToDelivery()
    } else {
      // Show and enable delivery address fields
      this.deliveryAddressTarget.classList.remove('d-none')
      this.enableFields(this.deliveryAddressTarget)
      this.sameAsMainFieldTarget.value = 'false'

      // Trigger geocoding for delivery address to update map
      setTimeout(() => {
        this.geocodeDeliveryAddress()
      }, 100)
    }
  }

  copyMainAddressToDelivery() {
    console.log('copyMainAddressToDelivery - copying fields and coordinates')

    // Get main address values
    const mainStreet = document.querySelector('input[name="client_registration[main_address][street]"]')?.value || ''
    const mainHouseNumber = document.querySelector('input[name="client_registration[main_address][house_number]"]')?.value || ''
    const mainApartmentNumber = document.querySelector('input[name="client_registration[main_address][apartment_number]"]')?.value || ''
    const mainPostalCode = document.querySelector('input[name="client_registration[main_address][postal_code]"]')?.value || ''
    const mainCity = document.querySelector('input[name="client_registration[main_address][city]"]')?.value || ''
    // Country is always NO (Norway)
    const mainCountry = 'NO'

    console.log('Main address:', { mainStreet, mainHouseNumber, mainApartmentNumber, mainPostalCode, mainCity, mainCountry })

    // Copy to delivery address fields (even though they're hidden)
    const deliveryStreet = document.querySelector('input[name="client_registration[delivery_addresses][][street]"]')
    const deliveryHouseNumber = document.querySelector('input[name="client_registration[delivery_addresses][][house_number]"]')
    const deliveryApartmentNumber = document.querySelector('input[name="client_registration[delivery_addresses][][apartment_number]"]')
    const deliveryPostalCode = document.querySelector('input[name="client_registration[delivery_addresses][][postal_code]"]')
    const deliveryCity = document.querySelector('input[name="client_registration[delivery_addresses][][city]"]')

    if (deliveryStreet) deliveryStreet.value = mainStreet
    if (deliveryHouseNumber) deliveryHouseNumber.value = mainHouseNumber
    if (deliveryApartmentNumber) deliveryApartmentNumber.value = mainApartmentNumber
    if (deliveryPostalCode) deliveryPostalCode.value = mainPostalCode
    if (deliveryCity) deliveryCity.value = mainCity
    // Country is set via hidden input, always NO

    // Geocode main address and update map with delivery coordinates
    this.geocodeMainAddressForDelivery()
  }

  geocodeMainAddressForDelivery() {
    let street = document.querySelector('input[name="client_registration[main_address][street]"]')?.value || ''
    const houseNumber = document.querySelector('input[name="client_registration[main_address][house_number]"]')?.value || ''
    const postalCode = document.querySelector('input[name="client_registration[main_address][postal_code]"]')?.value || ''
    const city = document.querySelector('input[name="client_registration[main_address][city]"]')?.value || ''
    // Country is always NO (Norway)
    const country = 'NO'

    // Clean street name - remove common prefixes
    street = this.cleanStreetName(street)

    console.log('geocodeMainAddressForDelivery - address:', { street, houseNumber, postalCode, city, country })

    if (!street || !city) {
      console.log('geocodeMainAddressForDelivery - missing street or city')
      alert('Please fill in street and city fields before updating the map.')
      return
    }

    const address = `${street} ${houseNumber}, ${postalCode} ${city}, ${country}`
    const encodedAddress = encodeURIComponent(address)
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`

    console.log('geocodeMainAddressForDelivery - fetching:', url)

    fetch(url)
      .then(response => response.json())
      .then(data => {
        console.log('geocodeMainAddressForDelivery - response:', data)
        if (data && data.length > 0) {
          const result = data[0]
          const lat = parseFloat(result.lat)
          const lng = parseFloat(result.lon)

          console.log('geocodeMainAddressForDelivery - coordinates:', { lat, lng })

          // Update map
          const mapController = this.application.getControllerForElementAndIdentifier(
            this.deliveryMapTarget,
            'map'
          )
          if (mapController && mapController.map) {
            console.log('geocodeMainAddressForDelivery - updating map')
            mapController.map.setView([lat, lng], 13)
            if (mapController.marker) {
              mapController.marker.setLatLng([lat, lng])
            }
            mapController.updateCoordinates(lat, lng)
          }
        } else {
          console.log('geocodeMainAddressForDelivery - no results found')
          alert('Could not find the address on the map. Please check the address or manually place the marker on the map by clicking.')
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error)
        alert('Error connecting to geocoding service. Please try again later.')
      })
  }

  geocodeDeliveryAddress() {
    let street = document.querySelector('input[name="client_registration[delivery_addresses][][street]"]')?.value || ''
    const houseNumber = document.querySelector('input[name="client_registration[delivery_addresses][][house_number]"]')?.value || ''
    const postalCode = document.querySelector('input[name="client_registration[delivery_addresses][][postal_code]"]')?.value || ''
    const city = document.querySelector('input[name="client_registration[delivery_addresses][][city]"]')?.value || ''
    // Country is always NO (Norway)
    const country = 'NO'

    // Clean street name - remove common prefixes
    street = this.cleanStreetName(street)

    console.log('geocodeDeliveryAddress - address:', { street, houseNumber, postalCode, city, country })

    if (!street || !city) {
      console.log('geocodeDeliveryAddress - missing street or city')
      alert('Please fill in street and city fields before updating the map.')
      return
    }

    const address = `${street} ${houseNumber}, ${postalCode} ${city}, ${country}`
    const encodedAddress = encodeURIComponent(address)
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`

    console.log('geocodeDeliveryAddress - fetching:', url)

    fetch(url)
      .then(response => response.json())
      .then(data => {
        console.log('geocodeDeliveryAddress - response:', data)
        if (data && data.length > 0) {
          const result = data[0]
          const lat = parseFloat(result.lat)
          const lng = parseFloat(result.lon)

          console.log('geocodeDeliveryAddress - coordinates:', { lat, lng })

          // Update map
          const mapController = this.application.getControllerForElementAndIdentifier(
            this.deliveryMapTarget,
            'map'
          )
          if (mapController && mapController.map) {
            console.log('geocodeDeliveryAddress - updating map')
            mapController.map.setView([lat, lng], 13)
            if (mapController.marker) {
              mapController.marker.setLatLng([lat, lng])
            }
            mapController.updateCoordinates(lat, lng)
          }
        } else {
          console.log('geocodeDeliveryAddress - no results found')
          alert('Could not find the address on the map. Please check the address or manually place the marker on the map by clicking.')
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error)
        alert('Error connecting to geocoding service. Please try again later.')
      })
  }

  cleanStreetName(street) {
    // Remove common street prefixes in different languages
    const prefixes = [
      'ul.', 'ul ', 'ulica ',
      'str.', 'str ', 'street ',
      'ave.', 'ave ', 'avenue ',
      'gt.', 'gt ', 'gate ',
      'vei ', 'veien '
    ]

    let cleaned = street.trim()
    for (const prefix of prefixes) {
      if (cleaned.toLowerCase().startsWith(prefix)) {
        cleaned = cleaned.substring(prefix.length).trim()
        break
      }
    }

    return cleaned
  }

  validateOrgNumber() {
    const orgNumber = this.organizationNumberTarget.value.replace(/\D/g, '')

    if (orgNumber.length === 0) {
      this.clearOrgNumberValidation()
      return
    }

    if (orgNumber.length !== 9) {
      this.showOrgNumberError('Organization number must be 9 digits')
      return
    }

    if (this.validateNorwegianOrgNumber(orgNumber)) {
      this.showOrgNumberSuccess()
    } else {
      this.showOrgNumberError('Invalid Norwegian organization number')
    }
  }

  validateNorwegianOrgNumber(orgNumber) {
    const weights = [3, 2, 7, 6, 5, 4, 3, 2]
    const digits = orgNumber.split('').map(Number)

    let sum = 0
    for (let i = 0; i < 8; i++) {
      sum += digits[i] * weights[i]
    }

    const remainder = sum % 11
    let checkDigit

    if (remainder === 0) {
      checkDigit = 0
    } else {
      checkDigit = 11 - remainder
    }

    if (checkDigit === 10) {
      return false
    }

    return checkDigit === digits[8]
  }

  showOrgNumberError(message) {
    const input = this.organizationNumberTarget
    const wrapper = input.closest('.mb-3')

    input.classList.remove('is-valid')
    input.classList.add('is-invalid')

    let feedback = wrapper.querySelector('.invalid-feedback')
    if (!feedback) {
      feedback = document.createElement('div')
      feedback.className = 'invalid-feedback'
      input.after(feedback)
    }
    feedback.textContent = message
  }

  showOrgNumberSuccess() {
    const input = this.organizationNumberTarget
    const wrapper = input.closest('.mb-3')

    input.classList.remove('is-invalid')
    input.classList.add('is-valid')

    const feedback = wrapper.querySelector('.invalid-feedback')
    if (feedback) {
      feedback.remove()
    }
  }

  clearOrgNumberValidation() {
    const input = this.organizationNumberTarget
    const wrapper = input.closest('.mb-3')

    input.classList.remove('is-invalid', 'is-valid')

    const feedback = wrapper.querySelector('.invalid-feedback')
    if (feedback) {
      feedback.remove()
    }
  }

  updateMainPhone() {
    if (!this.hasMainPhoneTarget || !this.hasMainPhoneFieldTarget) return

    // Get the phone number controller instance
    const phoneController = this.application.getControllerForElementAndIdentifier(
      this.mainPhoneTarget.closest('[data-controller~="phone-number"]'),
      'phone-number'
    )

    if (phoneController && phoneController.iti) {
      // Get the full international number from intl-tel-input
      const fullNumber = phoneController.iti.getNumber()
      this.mainPhoneFieldTarget.value = fullNumber
    }
  }

  updateMainPhoneDelayed() {
    // Wait for intl-tel-input to initialize
    setTimeout(() => {
      this.updateMainPhone()
    }, 500)
  }

  updateMapMarker() {
    console.log('updateMapMarker - manually triggered')

    // Check if "same as main" checkbox is checked
    const checkbox = document.getElementById('sameAsMain')
    const isChecked = checkbox && checkbox.checked

    if (isChecked) {
      console.log('updateMapMarker - using main address')
      // Use main address
      this.geocodeMainAddressForDelivery()
    } else {
      console.log('updateMapMarker - using delivery address')
      // Use delivery address
      this.geocodeDeliveryAddress()
    }
  }
}
